import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Users, ChevronRight } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Modal from '../../components/common/Modal';
import { generateId } from '../../utils/helpers';
import { getCurrentAcademicYear } from '../../utils/academicYear';

const inputCls = 'w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300';

function ClassList() {
  const { classes, staff, students, saveClass, updateClass, deleteClass, academicYear } = useApp();
  const [modal, setModal]   = useState(false);
  const [form, setForm]     = useState(() => emptyForm());
  const [editing, setEditing] = useState(null);
  const [lockedName, setLockedName] = useState(null); // when adding section to existing class

  function emptyForm(name = '') {
    return { name, section: '', roomNumber: '', capacity: '40', academicYear: academicYear || getCurrentAcademicYear(), classTeacherId: '' };
  }

  const openAddClass = () => {
    setForm(emptyForm());
    setLockedName(null);
    setEditing(null);
    setModal(true);
  };

  const openAddSection = (className) => {
    setForm(emptyForm(className));
    setLockedName(className);
    setEditing(null);
    setModal(true);
  };

  const openEdit = (c) => {
    setForm(c);
    setLockedName(null);
    setEditing(c);
    setModal(true);
  };

  const remove = async (id) => {
    if (window.confirm('Delete this section?')) await deleteClass(id);
  };

  const save = async () => {
    if (editing) {
      await updateClass(editing.id, { ...form, id: editing.id, subjectIds: editing.subjectIds || [], studentIds: editing.studentIds || [] });
    } else {
      await saveClass({ ...form, id: generateId('CLS'), subjectIds: [], studentIds: [] });
    }
    setModal(false);
  };

  const getTeacher   = (id) => { const s = staff.find(s => s.id === id); return s ? `${s.firstName} ${s.lastName}` : '—'; };
  const studentCount = (c)  => students.filter(s => s.classId === c.id).length;

  // Sort class names numerically
  const classNames = [...new Set(classes.map(c => c.name))].sort((a, b) => {
    const nA = parseInt(a.replace(/\D/g, '')) || 0;
    const nB = parseInt(b.replace(/\D/g, '')) || 0;
    return nA !== nB ? nA - nB : a.localeCompare(b);
  });

  const grouped = classNames.map(name => ({
    name,
    sections: classes.filter(c => c.name === name).sort((a, b) => a.section.localeCompare(b.section)),
  }));

  const teachers = staff.filter(s => s.designation === 'Teacher' || s.designation === 'Senior Teacher');

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Classes</h1>
          <p className="text-gray-500 text-sm">{classNames.length} classes · {classes.length} sections</p>
        </div>
        <button onClick={openAddClass} className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium">
          <Plus size={16} /> Add Class
        </button>
      </div>

      {classes.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <Users size={36} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">No classes yet. Click <strong>Add Class</strong> to get started.</p>
        </div>
      )}

      <div className="space-y-5">
        {grouped.map(group => (
          <div key={group.name} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Class group header */}
            <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-bold text-gray-800">{group.name}</h2>
                <span className="text-xs text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full">
                  {group.sections.length} section{group.sections.length !== 1 ? 's' : ''}
                </span>
                <span className="text-xs text-gray-400">
                  {group.sections.reduce((s, c) => s + studentCount(c), 0)} students
                </span>
              </div>
              <button
                onClick={() => openAddSection(group.name)}
                className="flex items-center gap-1 text-xs font-medium text-purple-600 hover:text-purple-800 border border-purple-200 hover:border-purple-400 bg-purple-50 hover:bg-purple-100 px-2.5 py-1 rounded-lg transition-colors"
              >
                <Plus size={12} /> Add Section
              </button>
            </div>

            {/* Sections table */}
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 border-b border-gray-50">
                  <th className="px-5 py-2 font-medium">Section</th>
                  <th className="px-4 py-2 font-medium">Room</th>
                  <th className="px-4 py-2 font-medium">Capacity</th>
                  <th className="px-4 py-2 font-medium">Students</th>
                  <th className="px-4 py-2 font-medium">Class Teacher</th>
                  <th className="px-4 py-2 font-medium">Academic Year</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {group.sections.map((sec, i) => (
                  <tr key={sec.id} className={`border-b border-gray-50 hover:bg-purple-50/30 transition-colors ${i === group.sections.length - 1 ? 'border-0' : ''}`}>
                    <td className="px-5 py-3">
                      <span className="bg-purple-100 text-purple-700 font-semibold text-xs px-2.5 py-1 rounded-full">
                        Section {sec.section}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{sec.roomNumber || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{sec.capacity || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1 text-gray-700 font-medium">
                        <Users size={13} className="text-gray-400" />
                        {studentCount(sec)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{getTeacher(sec.classTeacherId)}</td>
                    <td className="px-4 py-3 text-gray-400">{sec.academicYear}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => openEdit(sec)} className="p-1.5 hover:bg-purple-50 rounded-lg text-purple-500 hover:text-purple-700"><Edit2 size={14} /></button>
                        <button onClick={() => remove(sec.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Modal */}
      <Modal isOpen={modal} onClose={() => setModal(false)} title={editing ? 'Edit Section' : lockedName ? `Add Section — ${lockedName}` : 'Add New Class'}>
        <div className="space-y-3">
          <div className={lockedName ? 'opacity-50 pointer-events-none' : ''}>
            <label className="block text-sm font-medium mb-1">Class Name</label>
            <input
              value={form.name || ''}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className={inputCls}
              placeholder="e.g. Class 6, Nursery, LKG"
              readOnly={!!lockedName}
            />
            {lockedName && <p className="text-xs text-gray-400 mt-0.5">Class name is shared across all sections.</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Section</label>
              <input value={form.section || ''} onChange={e => setForm(f => ({ ...f, section: e.target.value }))}
                className={inputCls} placeholder="e.g. A, B, C" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Room Number</label>
              <input value={form.roomNumber || ''} onChange={e => setForm(f => ({ ...f, roomNumber: e.target.value }))}
                className={inputCls} placeholder="e.g. 101" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Capacity</label>
              <input type="number" value={form.capacity || ''} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))}
                className={inputCls} placeholder="40" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Academic Year</label>
              <input value={form.academicYear || ''} onChange={e => setForm(f => ({ ...f, academicYear: e.target.value }))}
                className={inputCls} placeholder="2025-2026" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Class Teacher</label>
            <select value={form.classTeacherId || ''} onChange={e => setForm(f => ({ ...f, classTeacherId: e.target.value }))} className={inputCls}>
              <option value="">Select Teacher</option>
              {teachers.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={() => setModal(false)} className="px-4 py-1.5 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={save} className="px-4 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">Save</button>
        </div>
      </Modal>
    </div>
  );
}

export default ClassList;
